import React from 'react';
import styled, { css } from 'styled-components';

import { basic, grey } from '../../theme/colors';
import { Tooltip } from './tooltip';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  tooltip?: string;
  children?: React.ReactNode | React.ReactNode[];
  variant?:
    | 'primary'
    | 'secondary'
    | 'warning'
    | 'ternary'
    | 'accept'
    | 'danger'
    | 'gray'
    | 'link'
    | 'likeText'
    | 'login';
  type?: 'button' | 'submit' | 'reset';
  block?: boolean;
  fullWidth?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  readonly?: boolean;
}

const basicStyle = {
  borderRadius: '8px',
  borderWidth: '1px',
  background: 'transparent',
  borderColor: 'transparent',
  borderStyle: 'solid',
  height: '40px',
  cursor: 'pointer',
  margin: 0,
  whiteSpace: 'nowrap',
  color: grey.haydenGreyShade1,
  fontWeight: '400',
  display: 'inline-block',
};

const variantStyles = (variant = 'primary') =>
  ({
    primary: css`
      ${basicStyle}
      color: white;
      border-color: ${basic.haydenAccent};
      background: ${basic.haydenAccent};
      &:hover {
        background: ${basic.haydenGhostHover};
        border-color: ${basic.haydenGhostHover};
      }
      &:focus {
        background: ${basic.haydenGhostFocus};
        border-color: ${basic.haydenGhostHover};
      }
      &:disabled,
      &[disabled] {
        background: ${basic.haydenGhostDisabled};
        border-color: ${basic.haydenGhostDisabled};
        color: #ededed;
      }
    `,
    secondary: css`
      ${basicStyle}
      color:  ${basic.haydenAccent};
      border-color: ${basic.haydenBorder};
      background: ${basic.haydenBorder};
      &:hover {
        background: ${basic.haydenLightSteel3};
        border-color: ${basic.haydenLightSteel3};
      }
      &:focus {
        background: ${basic.haydenLightSteel4};
        border-color: ${basic.haydenLightSteel4};
      }
      &:disabled,
      &[disabled] {
        opacity: 0.6;
      }
    `,
    ternary: css`
      ${basicStyle}
      color: ${basic.haydenAccent};
      border-color: ${basic.haydenAccent};
      background-color: transparent;

      &:hover {
        color: ${basic.haydenGhostHover};
        border-color: ${basic.haydenGhostHover};
      }
      &:focus {
        color: ${basic.haydenGhostFocus};
        border-color: ${basic.haydenGhostFocus};
      }
      &:disabled,
      &[disabled] {
        color: ${basic.haydenGhostDisabled};
        border-color: ${basic.haydenGhostDisabled};
      }
    `,
    accept: css`
      ${basicStyle}
      color: ${basic.haydenWhite};
      background-color: ${basic.haydenGreen};
      border-color: ${basic.haydenGreen};
      &:hover,
      &:focus {
        background: lighten(${basic.haydenGreen}, 10%);
        border-color: lighten(${basic.haydenGreen}, 10%);
      }
      &:disabled,
      &[disabled] {
        opacity: 0.6;
      }
    `,
    danger: css`
      ${basicStyle}
      color: ${basic.haydenWhite};
      background-color: ${basic.haydenRed};
      border-color: ${basic.haydenRed};
      &:hover,
      &:focus {
        background: lighten(${basic.haydenRed}, 10%);
        border-color: lighten(${basic.haydenRed}, 10%);
      }
      &:disabled,
      &[disabled] {
        opacity: 0.6;
      }
    `,
    warning: css`
      ${basicStyle}
      color: ${basic.haydenWhite};
      background-color: ${basic.haydenOrange};
      border-color: ${basic.haydenOrange};
      &:hover,
      &:focus {
        background: lighten(${basic.haydenOrange}, 10%);
        border-color: lighten(${basic.haydenOrange}, 10%);
      }
      &:disabled,
      &[disabled] {
        opacity: 0.6;
      }
    `,
    gray: css`
      ${basicStyle}
      color: #fff;
      background-color: ${basic.haydenGray};
      border-color: ${basic.haydenGray};
    `,
    link: css`
      ${basicStyle}
      border: none;
      color: ${grey.haydenGreyShade1};
      font-size: 16px;
      line-height: 24px;
      color: ${basic.haydenAccent};
      background: transparent;
      cursor: pointer;
      transition: 150ms linear all;
      &:disabled {
        opacity: 0.3;
      }
    `,
    likeText: css`
      ${basicStyle}
      border: none;
      transition: 150ms linear all;
      &:disabled {
        opacity: 0.3;
      }
    `,
    login: css`
      ${basicStyle}
      color: white;
      border-color: ${basic.haydenLogin};
      background: ${basic.haydenLogin};
      display: flex;
      flex-direction: row;
      justify-content: center;
      &:hover {
        background: ${basic.haydenAccent};
        border-color: ${basic.haydenAccent};
      }
      &:focus {
        background: ${basic.haydenAccent};
        border-color: ${basic.haydenAccent};
      }
      &:disabled,
      &[disabled] {
        opacity: 0.6;
      }
      img {
        margin-right: 0.5em;
      }
    `,
  })[variant];

const sizeStyles = (size = 'md') =>
  ({
    sm: css`
      padding: 4px 8px;
      height: auto;
      font-size: 0.75rem;
    `,
    md: css`
      font-size: 16px;
      line-height: 24px;
      padding: 7px 16px;
    `,
    lg: css`
      padding: 8px 24px;
      height: auto;
      font-size: 1.25rem;
    `,
    xs: css`
      padding: 0;
      height: auto;
      font-size: 16px;
      line-height: 24px;
    `,
  })[size];

const StyledButton = styled.button.attrs({ tabIndex: 0, role: 'button' })<ButtonProps>`
  & > input[type='file'] {
    display: none;
  }
  transition: 150ms linear all;

  ${({ block }) =>
    block &&
    css`
      display: block;
    `};

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `};

  ${({ variant }) => variantStyles(variant)}
  ${({ size }) => sizeStyles(size)}
  ${({ readonly }) =>
    readonly &&
    css`
      pointer-events: none;
      cursor: default;
    `}

  .spinner-border.spinner-border-sm {
    position: relative;
    top: 3px;
  }

  &:disabled {
    cursor: default;
  }
`;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  if (props.tooltip) {
    return (
      <Tooltip tooltip={props.tooltip}>
        <StyledButton ref={ref} type={props.type || 'button'} {...props}>
          {props.children}
        </StyledButton>
      </Tooltip>
    );
  }

  return (
    <StyledButton ref={ref} type={props.type || 'button'} {...props}>
      {props.children}
    </StyledButton>
  );
});
